import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, Switch } from 'components/ui';
import { useSearchParams } from 'react-router-dom';
import Select from 'react-select';
import { Input, Textarea } from 'components/ui/Form';
import {
  PlusIcon,
  PencilIcon,
  StarIcon,
  ClockIcon,
  UserIcon,
  XMarkIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { Fragment } from 'react';
import { getLeadNotes, addLeadNote, editLeadNote } from 'services/notesService';
import { toast } from 'sonner';
import { hasPermission } from 'utils/permissionsUtils';
import { formatDisplayDate } from "../../../utils/dateUtils";
import InfiniteScroll from "react-infinite-scroll-component";

const NotesTab = ({ isMobile, leadData, openAddModalExternal = false, setOpenAddModalExternal = null }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [isImportant, setIsImportant] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showImportantOnly, setShowImportantOnly] = useState(false);
  const [editingNote, setEditingNote] = useState(null);

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(10); // or whatever you want
  const [totalRecords, setTotalRecords] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  // Get lead ID from leadData prop
  const leadId = leadData?.id || leadData?.leadId || leadData?.LeadId;
  const scrollElementId = `notes-scroll-${leadId}`;
  const [search, setSearch] = useState("");

  const SOURCE_TYPE_OPTIONS = [
    { value: 'ALL', label: 'All Sources' },
    { value: 'Note', label: 'Manual Notes' },
    { value: 'STAGE_CHANGE', label: 'Stage Changes' },
    { value: 'FOLLOW_UP', label: 'Follow Ups' },
    { value: 'QUERY_ASSIGNMENT', label: 'Query Assignments' },
    { value: 'DOCUMENT_VERIFIED', label: 'Document Verified' },
    { value: 'DOCUMENT_REJECTED', label: 'Document Rejected' }
  ];

  const [selectedSourceType, setSelectedSourceType] = useState(SOURCE_TYPE_OPTIONS[0]);
  const [searchParams] = useSearchParams();
  const targetNoteId = searchParams.get('noteId');

  // Effect to scroll to specific note if noteId is in URL
  useEffect(() => {
    if (targetNoteId && notes.length > 0) {
      // Small timeout to ensure DOM is fully ready
      const timer = setTimeout(() => {
        // Try all possible ID match patterns
        const element = document.getElementById(`note-card-${targetNoteId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          
          // Temporary highlight effect
          element.classList.add('ring-2', 'ring-primary-500', 'ring-offset-2', 'dark:ring-offset-dark-800');
          
          const removeHighlight = setTimeout(() => {
            element.classList.remove('ring-2', 'ring-primary-500', 'ring-offset-2', 'dark:ring-offset-dark-800');
          }, 4000);
          
          return () => clearTimeout(removeHighlight);
        }
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [targetNoteId, notes]);

  // Load notes when component mounts, leadId changes, or filter changes
  useEffect(() => {
    if (leadId) {
      setPageNumber(1);
      loadNotes(1, false);
    }
  }, [leadId, showImportantOnly]);

  // Sync external modal control
  useEffect(() => {
    if (openAddModalExternal) {
      setShowAddModal(true);
      // Reset the external state after opening
      if (setOpenAddModalExternal) {
        setOpenAddModalExternal(false);
      }
    }
  }, [openAddModalExternal, setOpenAddModalExternal]);

  const loadNotes = async (page = 1, append = false) => {
    if (!leadId) return;

    try {
      setLoading(true);

      const result = await getLeadNotes(leadId, {
        pageNumber: page,
        pageSize: pageSize,
        sortOrder: "desc",
        isImportant: showImportantOnly || null,
        search: search // Pass search term to API
      });

      if (result.success) {
        const newNotes = result.data || [];
        const total = result.totalRecords || 0;
        setTotalRecords(total);

        setNotes((prev) => {
          const updatedNotes = append ? [...prev, ...newNotes] : newNotes;
          setHasMore(updatedNotes.length < total && newNotes.length > 0);
          return updatedNotes;
        });

        if (append) {
          setPageNumber((prev) => prev + 1);
        } else {
          setPageNumber(2); // Next page will be 2 if not appending
        }
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load notes."); // Set error state
    } finally {
      setLoading(false);
    }
  };

  const loadMoreNotes = () => {
    loadNotes(pageNumber, true);
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast.error("Please enter a note", {
        description: "Note description cannot be empty",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      if (editingNote) {
        // Edit existing note
        const result = await editLeadNote(
          editingNote.Id || editingNote.NoteId,
          newNote.trim(),
          isImportant,
          editingNote.Status || "Active"
        );

        if (result.success) {
          toast.success("Note updated successfully!");
          // Reset form and reload notes
          resetModalState();
          await loadNotes(1, false); // Reload from first page
        } else {
          toast.error("Failed to update note", { description: result.message || "An unexpected error occurred." });
        }
      } else {
        // Add new note
        const result = await addLeadNote(leadId, newNote, isImportant);

        if (result.success) {
          toast.success("Note added successfully!");
          // Reset form and reload notes
          resetModalState();
          await loadNotes(1, false); // Reload from first page
        } else {
          toast.error("Failed to add note", { description: result.message || "An unexpected error occurred." });
        }
      }
    } catch (err) {
      console.error("Error handling note:", err);
      toast.error(editingNote ? "Failed to edit note" : "Failed to add note");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete note (new function)
  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Are you sure you want to delete this note?")) {
      return;
    }
    try {
      // Assuming a delete service function exists
      // const result = await deleteLeadNote(noteId);
      // if (result.success) {
      //   toast.success("Note deleted successfully!");
      //   await loadNotes(1, false);
      // } else {
      //   toast.error("Failed to delete note", { description: result.message || "An unexpected error occurred." });
      // }
      toast.info("Delete functionality not implemented yet."); // Placeholder
    } catch (err) {
      console.error("Error deleting note:", err);
      toast.error("Failed to delete note");
    }
  };


  // Define what makes a note 'Important' according to user requirement
  const isNoteImportant = (note) => {
    return Boolean(note.IsImportant) && (note.SourceType === 'Note' || note.SourceType === 'NOTE');
  };

  // Filtered notes for display
  const displayedNotes = notes.filter(note => {
    // 1. Important Toggle Filter
    if (showImportantOnly && !isNoteImportant(note)) {
      return false;
    }

    // 2. Source Type Filter
    if (selectedSourceType.value !== 'ALL') {
      if (selectedSourceType.value === 'Note') {
        // Manual notes can be 'Note' or 'NOTE'
        if (note.SourceType !== 'Note' && note.SourceType !== 'NOTE') return false;
      } else {
        if (note.SourceType !== selectedSourceType.value) return false;
      }
    }

    return true;
  });

  // Header count for currently visible notes
  const displayedCount = displayedNotes.length;

  // Handle edit note
  const handleEditNote = (note) => {
    setEditingNote(note);
    setNewNote(note.NoteText || note.NoteDescription || "");
    setIsImportant(Boolean(note.IsImportant));
    setShowAddModal(true);
  };

  // Reset modal state
  const resetModalState = () => {
    setShowAddModal(false);
    setEditingNote(null);
    setNewNote("");
    setIsImportant(false);
    setIsSubmitting(false);
  };

  // Loading state removed - handled by global loader

  if (error) {
    return (
      <Card className={`${isMobile ? "p-4" : "p-6"}`}>
        <div className="text-center py-8">
          <ExclamationCircleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Error Loading Notes
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">{error}</p>
          <Button onClick={() => loadNotes(1, false)} variant="outlined" color="primary">
            Try Again
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6 flex flex-col h-full">
      {/* Header */}
      <div className="flex flex-col space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Notes
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Track important conversations and observations
            </p>
          </div>
          {hasPermission("MLLE") ? (
            <Button
              onClick={() => setShowAddModal(true)}
              color="primary"
              disabled={leadData?.IsStudent}
              className={`flex items-center space-x-2 ${leadData?.IsStudent ? "opacity-60 cursor-not-allowed" : ""}`}
            >
              <PlusIcon className="w-4 h-4" />
              <span>Add Note</span>
            </Button>
          ) : null}
        </div>

        {/* Filter Options */}      <div className="flex flex-col space-y-4 rounded-xl bg-gray-50 p-4 dark:bg-dark-800/50 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        {/* Left: Toggle */}
        <div className="flex items-center space-x-3">
          <div className="flex items-center">
            <Switch
              checked={showImportantOnly}
              onClick={() => setShowImportantOnly(!showImportantOnly)}
              onCheckedChange={(checked) => setShowImportantOnly(checked)}
            />
          </div>
          <label
            className="text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Show important only
          </label>
        </div>

        {/* Right: Filter & Count */}
        <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:space-x-4 sm:space-y-0">
          <div className="w-full sm:w-56">
            <Select
              value={selectedSourceType}
              onChange={(option) => setSelectedSourceType(option || SOURCE_TYPE_OPTIONS[0])}
              options={SOURCE_TYPE_OPTIONS}
              classNamePrefix="react-select"
              placeholder="Filter by source..."
              styles={{
                control: (base, state) => ({
                  ...base,
                  backgroundColor: 'transparent',
                  borderColor: state.isFocused ? '#3b82f6' : '#e5e7eb',
                  borderRadius: '0.75rem',
                  fontSize: '0.875rem',
                  '&:hover': {
                    borderColor: '#3b82f6'
                  }
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 50,
                  borderRadius: '0.75rem',
                  overflow: 'hidden'
                }),
                option: (base, state) => ({
                  ...base,
                  fontSize: '0.875rem',
                  backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#f3f4f6' : 'white',
                  color: state.isSelected ? 'white' : '#374151'
                })
              }}
            />
          </div>

          <div className="text-right text-sm text-gray-500 dark:text-gray-400 min-w-max">
            {showImportantOnly || selectedSourceType.value !== 'ALL'
              ? `${displayedCount} filtered note${displayedCount !== 1 ? "s" : ""}`
              : `${notes.length} total note${notes.length !== 1 ? "s" : ""}`}
          </div>
        </div>
      </div>
    </div>

      {/* Notes List with Scroll Container */}
      <div className="flex-1 overflow-hidden">
        <div
          id={scrollElementId}
          className="h-full overflow-y-auto px-1"
          style={{ height: "calc(100vh - 20rem)", minHeight: "400px" }}
        >
          {notes.length === 0 ? (
            <Card className={`${isMobile ? "p-4" : "p-8"} text-center`}>
              <div
                className={`${isMobile ? "h-12 w-12" : "h-16 w-16"} mx-auto mb-4 flex items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/20`}
              >
                <PencilIcon
                  className={`${isMobile ? "w-6 h-6" : "w-8 h-8"} text-amber-600 dark:text-amber-400`}
                />
              </div>
              <h3
                className={`${isMobile ? "text-base" : "text-lg"} dark:text-dark-50 mb-2 font-semibold text-gray-900`}
              >
                {showImportantOnly ? "No Important Notes" : "No Notes Yet"}
              </h3>
              <p
                className={`${isMobile ? "text-sm" : "text-base"} dark:text-dark-300 mb-6 text-gray-500`}
              >
                {showImportantOnly
                  ? "No important notes found for this lead. Try turning off the filter or mark notes as important."
                  : "Start adding notes to track important information about this lead"}
              </p>
              {hasPermission("MLLE") ? (
                <Button
                  onClick={() => setShowAddModal(true)}
                  disabled={leadData?.IsStudent}
                  variant="soft"
                  color="primary"
                  className={`${isMobile ? "px-4 py-2 text-sm" : ""} ${leadData?.IsStudent ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Add First Note
                </Button>
              ) : null}
            </Card>
          ) : (
            <InfiniteScroll
              dataLength={displayedNotes.length}
              next={loadMoreNotes}
              hasMore={hasMore}
              scrollableTarget={scrollElementId}
              loader={
                <div className="flex justify-center py-4">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-500 border-t-transparent"></div>
                    <span>Loading more notes...</span>
                  </div>
                </div>
              }
              endMessage={
                notes.length > 0 && (
                  <div className="py-6 text-center text-gray-500">
                    No more notes
                  </div>
                )
              }
            >
              <div className="space-y-4">
                {displayedNotes.map((note, index) => (
                  <Card
                    key={note.LeadNoteId || note.NoteId || note.Id || index}
                    id={`note-card-${note.LeadNoteId || note.NoteId || note.Id}`}
                    className="group relative overflow-hidden border border-gray-200 transition-all duration-200 hover:border-gray-300 hover:shadow-lg dark:border-gray-700 dark:hover:border-gray-600"
                  >
                    {/* Important indicator stripe */}
                    {isNoteImportant(note) && (
                      <div className="absolute bottom-0 left-0 top-0 w-1 bg-gradient-to-b from-yellow-400 to-yellow-600"></div>
                    )}
                    <div className="m-1.5">
                      {note.SourceType && (
                        <div className="flex items-center space-x-1">
                          <Badge
                            variant="flat"
                            className={`${
                              note.SourceType === "FOLLOW_UP"
                                ? "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400"
                                : note.SourceType === "STAGE_CHANGE"
                                  ? "border-purple-200 bg-purple-50 text-purple-700 dark:border-purple-800 dark:bg-purple-900/20 dark:text-purple-400"
                                  : "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-400"
                            } py-0.5 text-[10px] font-bold uppercase tracking-wider`}
                          >
                            {note.SourceType.replace("_", " ")}
                          </Badge>
                        </div>
                      )}
                    </div>

                    <div className="p-2">
                      <div className="mb-4 flex items-start justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                            <ClockIcon className="w-4 h-4" />
                            <span className="font-medium">
                              {formatDisplayDate(
                                note.ActivityDate || note.CreatedDate,
                              )}
                            </span>
                          </div>

                          {note.CreatedByName && (
                            <div className="flex items-center space-x-1 text-sm text-gray-600 dark:text-gray-300">
                              <span>•</span>
                              <UserIcon className="w-4 h-4" />
                              <span className="font-medium">
                                by {note.CreatedByName}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="flex items-center space-x-2">
                          {isNoteImportant(note) && (
                            <div className="flex items-center space-x-1.5 rounded-full border border-yellow-200 bg-gradient-to-r from-yellow-50 to-amber-50 px-3 py-1.5 text-xs font-semibold text-yellow-700 dark:border-yellow-800 dark:from-yellow-900/30 dark:to-amber-900/30 dark:text-yellow-400">
                              <StarSolid className="h-3.5 w-3.5" />
                              <span>Important</span>
                            </div>
                          )}

                          {hasPermission("MLLE") && (note.SourceType === 'Note' || note.SourceType === 'NOTE') && !leadData?.IsStudent ? (
                            <Button
                              onClick={() => handleEditNote(note)}
                              variant="flat"
                              size="sm"
                              className="rounded-lg p-2 text-gray-400 opacity-0 transition-opacity duration-200 hover:bg-gray-100 hover:text-primary-600 group-hover:opacity-100 dark:text-gray-500 dark:hover:bg-gray-800 dark:hover:text-primary-400"
                            >
                              <PencilIcon className="w-4 h-4" />
                            </Button>
                          ) : null}
                        </div>
                      </div>

                      <div className="prose prose-sm max-w-none">
                        <p className="whitespace-pre-wrap text-base font-normal leading-relaxed text-gray-800 dark:text-gray-200">
                          {note.NoteText ||
                            note.NoteDescription ||
                            "No content"}
                        </p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </InfiniteScroll>
          )}
        </div>
      </div>

      {/* Add Note Modal */}
      <Transition appear show={showAddModal} as={Fragment}>
        <Dialog as="div" className="fixed inset-0 z-50 flex items-center justify-center p-4" onClose={() => { }}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50" />
          </TransitionChild>

          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className="relative w-full max-w-md max-h-[90vh] transform overflow-hidden rounded-xl bg-white shadow-2xl transition-all dark:bg-dark-800 border border-gray-200 dark:border-dark-600">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <DialogTitle
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 dark:text-white"
                  >
                    {editingNote ? "Edit Note" : "Add New Note"}
                  </DialogTitle>
                  <Button
                    onClick={resetModalState}
                    variant="flat"
                    isIcon
                    className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </Button>
                </div>

                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Note
                    </label>
                    <Textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Enter your note here..."
                      rows={6}
                      className="w-full resize-none min-h-[140px]"
                      autoFocus
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-full transition-colors duration-200 ${isImportant ? 'bg-yellow-100 dark:bg-yellow-900/30' : 'bg-gray-200 dark:bg-gray-700'}`}>
                        {isImportant ? (
                          <StarSolid className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                        ) : (
                          <StarIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                        )}
                      </div>
                      <div>
                        <label
                          htmlFor="isImportant"
                          onClick={() => setIsImportant(!isImportant)}
                          className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer"
                        >
                          Mark as important
                        </label>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {isImportant ? "This note will be highlighted" : "This note will be regular"}
                        </p>
                      </div>
                    </div>
                    <Switch
                      id="isImportant"
                      checked={isImportant}
                      onCheckedChange={(event) => {
                        const checked = event.target ? event.target.checked : event;
                        setIsImportant(checked);
                      }}
                      onChange={(event) => {
                        const checked = event.target ? event.target.checked : event;
                        setIsImportant(checked);
                      }}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end space-x-3 mt-6">
                  <Button
                    onClick={resetModalState}
                    variant="outlined"
                    color="neutral"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAddNote}

                    color="primary"
                    disabled={isSubmitting || !newNote.trim()}
                    className="min-w-[100px]"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center">
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                        {editingNote ? "Updating..." : "Adding..."}
                      </div>
                    ) : (
                      editingNote ? "Update Note" : "Add Note"
                    )}
                  </Button>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </Transition>
    </div>
  );
};

export default NotesTab;